import dayjs from "dayjs";
import {
  DatafeedQuoteValues,
  QuoteData,
  QuotesCallback,
  QuotesErrorCallback,
} from "../charting_library/charting_library";
import { CODE, MODE, Resolutions, Segment } from "../constants";
import { Tick } from "../types";
import {
  error,
  extractDataFromSymbolName,
  getBarsFromBackend,
  getSegment,
  log,
} from "./helpers";
import { emit } from "./socket";
import Store from "./store";

interface QuoteSubscription {
  symbols: string[];
  callback: QuotesCallback;
}

// listenerGUID > subscription
const quoteSubscriptions = new Map<string, QuoteSubscription>();

// symbolToken > most recent quote values
const quoteCache = new Map<number, DatafeedQuoteValues>();

/* ------------------------------------------------------------------
  Returns the instrument token of a symbol.
  Quotes can be requested before resolveSymbol() cached the token,
  hence the fallback to the search index.
*/
const getSymbolToken = (symbolName: string): number | undefined => {
  const cachedToken = Store.getTokenFromTicker(symbolName);
  if (cachedToken) {
    return cachedToken;
  }

  const [_segment2, exchange, ticker] = extractDataFromSymbolName(symbolName);
  const instrument = Store.getSearchInstance().get(
    ticker,
    getSegment(ticker, exchange),
    exchange
  );

  return instrument.instrumentToken || undefined;
};

// open interest series carry OI values, a price change would be meaningless there
const isOpenInterestSymbol = (symbolName: string) =>
  extractDataFromSymbolName(symbolName)[3];

/* ------------------------------------------------------------------
  Quotes are requested by plain symbol name, but the library keys the
  subscription by the encoded extended symbol and silently drops updates
  whose name does not match. Answering under both names covers either key.
*/
const buildQuoteEntries = (
  symbolName: string,
  values?: DatafeedQuoteValues
): QuoteData[] => {
  const quoteValues = values || {};

  return [
    { s: "ok", n: symbolName, v: quoteValues },
    { s: "ok", n: `=${JSON.stringify({ symbol: symbolName })}`, v: quoteValues },
  ];
};

// prices arrive from the socket in paise
const valuesFromTick = (tick: Tick): DatafeedQuoteValues => {
  const lastPrice = tick.ltp!;
  const values: DatafeedQuoteValues = { lp: lastPrice / 100 };

  if (tick.volume) values.volume = tick.volume;
  if (tick.open) values.open_price = tick.open / 100;
  if (tick.high) values.high_price = tick.high / 100;
  if (tick.low) values.low_price = tick.low / 100;

  // tick.close holds the previous day's close
  if (tick.close) {
    values.prev_close_price = tick.close / 100;
    values.ch = (lastPrice - tick.close) / 100;
    values.chp = ((lastPrice - tick.close) / tick.close) * 100;
  }

  return values;
};

/* ------------------------------------------------------------------
  Derives the last day change from the two most recent daily bars.
  Needed because outside market hours no tick ever arrives, and the
  legend would otherwise show a zero change.
*/
const getQuoteValuesFromBars = async (
  symbolName: string,
  token: number
): Promise<DatafeedQuoteValues | undefined> => {
  const [segment2, exchange] = extractDataFromSymbolName(symbolName);

  // !Correction. If 'INDICES' we are using exchange as 'NSE' in backend
  let backendExchange = <Segment>exchange;
  if (backendExchange === Segment.INDICES) {
    backendExchange = <Segment>(segment2 || Segment.NSE);
  }

  const to = dayjs();

  try {
    const bars = await getBarsFromBackend(
      backendExchange,
      token,
      Resolutions["1D"],
      to.subtract(15, "day").unix(),
      to.unix(),
      false
    );

    if (bars.length < 2) {
      return undefined;
    }

    const lastClose = bars[bars.length - 1][4] / 100;
    const previousClose = bars[bars.length - 2][4] / 100;

    if (!previousClose) {
      return undefined;
    }

    return {
      lp: lastClose,
      prev_close_price: previousClose,
      ch: lastClose - previousClose,
      chp: ((lastClose - previousClose) / previousClose) * 100,
    };
  } catch (err) {
    error("[😰 getQuotes] Failed fetching daily bars:", err);
    return undefined;
  }
};

// getQuotes ----------------------------------------------------------
export const getQuotes = async (
  symbols: string[],
  onDataCallback: QuotesCallback,
  onErrorCallback: QuotesErrorCallback
) => {
  log("[💬 getQuotes]: Method call, Symbols:", symbols);

  try {
    const quotes = await Promise.all(
      symbols.map(async (symbolName) => {
        const token = getSymbolToken(symbolName);

        if (!token || isOpenInterestSymbol(symbolName)) {
          return buildQuoteEntries(symbolName);
        }

        let values = quoteCache.get(token);
        if (!values) {
          values = await getQuoteValuesFromBars(symbolName, token);
          if (values) {
            quoteCache.set(token, values);
          }
        }

        return buildQuoteEntries(symbolName, values);
      })
    );

    onDataCallback((<QuoteData[]>[]).concat(...quotes));
  } catch (err) {
    error("[😰 getQuotes]:", err);
    onErrorCallback(String(err));
  }
};

// subscribeQuotes ---------------------------------------------------
export const subscribeQuotes = (
  symbols: string[],
  fastSymbols: string[],
  onRealtimeCallback: QuotesCallback,
  listenerGUID: string
) => {
  log("[🔔 subscribeQuotes]: Method call, listenerGUID:", listenerGUID);

  const allSymbols = Array.from(new Set([...symbols, ...fastSymbols]));

  quoteSubscriptions.set(listenerGUID, {
    symbols: allSymbols,
    callback: onRealtimeCallback,
  });

  allSymbols.forEach((symbolName) => {
    const token = getSymbolToken(symbolName);
    if (!token) return;

    emit({
      code: CODE.SUB,
      mode: MODE.FULL,
      full: [token],
    });
  });
};

// unsubscribeQuotes -------------------------------------------------
export const unsubscribeQuotes = (listenerGUID: string) => {
  log("[🔕 unsubscribeQuotes]: Method call, listenerGUID:", listenerGUID);

  const subscription = quoteSubscriptions.get(listenerGUID);
  if (!subscription) return;

  quoteSubscriptions.delete(listenerGUID);

  subscription.symbols.forEach((symbolName) => {
    const token = getSymbolToken(symbolName);
    // bar subscriptions and other quote listeners keep needing the ticks
    if (!token || isTokenInUse(token)) return;

    emit({
      code: CODE.UNSUB,
      mode: MODE.FULL,
      full: [token],
    });
  });
};

const isTokenInUse = (token: number): boolean => {
  if (Store.getAllSocketSubscriptions().has(token)) {
    return true;
  }

  for (const subscription of quoteSubscriptions.values()) {
    if (subscription.symbols.some((s) => getSymbolToken(s) === token)) {
      return true;
    }
  }

  return false;
};

/* updateQuoteSubscribers -------------------------------------------
  Pushes the latest tick to every quote listener of that symbol.
*/
export const updateQuoteSubscribers = (tick: Tick) => {
  // invalid tick
  if (!tick.token || !tick.ltp) return;

  const values = valuesFromTick(tick);
  quoteCache.set(tick.token, values);

  quoteSubscriptions.forEach((subscription) => {
    const quotes: QuoteData[] = [];

    subscription.symbols.forEach((symbolName) => {
      if (
        isOpenInterestSymbol(symbolName) ||
        getSymbolToken(symbolName) !== tick.token
      ) {
        return;
      }

      quotes.push(...buildQuoteEntries(symbolName, values));
    });

    if (quotes.length) {
      subscription.callback(quotes);
    }
  });
};
